import { useState } from 'react'

import { type PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { useQueryPrimaryCategories } from '../../models/useQueryPrimaryCategories'

interface UseFirstPrimaryCategoryPresenterReturn {
  data: {
    firstPrimaryCategory: PrimaryCategory | undefined
  }
  states: {
    isFetching: boolean
    isError: boolean
    isEmpty: boolean
  }
}

/**
 * Provides the tenant's first PrimaryCategory so the Feature Set can
 * auto-select it on page load (BHV-002). Empty tenant → `undefined`.
 *
 * "First" is the lowest pk, as the backend's default ordering gives the
 * legacy page. Every pagination field is pinned because the shared model
 * merges the URL's `page`/`search`/`order_by` and the list's `name` default.
 * `isError` holds only while no page has loaded; a later success clears it.
 * `isEmpty` means a page loaded and the tenant has no PrimaryCategories.
 */
export const useFirstPrimaryCategoryPresenter =
  (): UseFirstPrimaryCategoryPresenterReturn => {
    const [hasFailed, setHasFailed] = useState(false)

    const { primaryCategories, pagination, isFetching } =
      useQueryPrimaryCategories({
        onError: () => setHasFailed(true),
        params: {
          page: 1,
          pageSize: 1,
          orderBy: 'id',
          search: undefined,
          simple: true,
        },
      })

    return {
      data: {
        firstPrimaryCategory: primaryCategories[0],
      },
      states: {
        isFetching,
        isError: hasFailed && !isFetching && pagination === undefined,
        isEmpty: !isFetching && pagination?.count === 0,
      },
    }
  }
